import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

const initialState = {
  list: [],
  pagination: null,
  details: null,
  isLoading: false,
  error: null,
  success: null,
};

// get boreholes
export const getBoreholes = createAsyncThunk(
  "boreholes/getBoreholes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);

      const url = `${BASE_URL}/boreholes${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get boreholes!";
      return rejectWithValue(message);
    }
  },
);

// add borehole
export const addBorehole = createAsyncThunk(
  "boreholes/addBorehole",
  async (boreholeData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // Automatically determine content type based on FormData
      const isFormData = boreholeData instanceof FormData;

      const response = await axios.post(`${BASE_URL}/boreholes`, boreholeData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add borehole!";
      return rejectWithValue(message);
    }
  },
);

// update borehole
export const updateBorehole = createAsyncThunk(
  "boreholes/updateBorehole",
  async ({ id, boreholeData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const isFormData = boreholeData instanceof FormData;

      // If we are sending FormData for a PUT request, some frameworks prefer POST with _method=PUT natively inside formData
      // Or we can just use PUT logic, assuming backend handles FormData on PUT correctly if _method is not needed.
      // As per instructions, "PUT /api/boreholes/{id} أو POST مع _method=PUT لو هتبعت ملفات."
      // Let's use standard POST with _method=PUT inside the FormData for files.
      // Wait, let's keep it simple. The user will handle setting `_method=PUT` if they use FormData in the component.

      const response = await axios.post(
        `${BASE_URL}/boreholes/${id}`,
        boreholeData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update borehole!";
      return rejectWithValue(message);
    }
  },
);

// delete borehole
export const deleteBorehole = createAsyncThunk(
  "boreholes/deleteBorehole",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/boreholes/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete borehole!";
      return rejectWithValue(message);
    }
  },
);

// get borehole details
export const getBoreholeDetails = createAsyncThunk(
  "boreholes/getBoreholeDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/boreholes/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get borehole details!";
      return rejectWithValue(message);
    }
  },
);

// boreholes slice
const boreholesSlice = createSlice({
  name: "boreholes",
  initialState,
  reducers: {
    hydrate: (state, action) => {
      // Hydrate state from persisted storage
      if (action.payload) {
        return { ...state, ...action.payload };
      }
      return state;
    },
    clearBoreholesError: state => {
      state.error = null;
    },
    clearBoreholesSuccess: state => {
      state.success = null;
    },
    clearBoreholeDetails: state => {
      state.details = null;
    },
    setBoreholes: (state, action) => {
      state.list = action.payload;
    },
    setBoreholesPagination: (state, action) => {
      state.pagination = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getBoreholes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBoreholes.fulfilled, (state, action) => {
        state.isLoading = false;

        const data = action.payload;
        state.list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(action.payload?.data)
              ? action.payload.data
              : [];
        state.pagination = data?.meta || action.payload?.meta || null;
        state.error = null;
      })
      .addCase(getBoreholes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addBorehole.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBorehole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم إضافة الجسة بنجاح";
        state.error = null;
      })
      .addCase(addBorehole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateBorehole.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBorehole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم تحديث الجسة بنجاح";
        state.error = null;
      })
      .addCase(updateBorehole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteBorehole.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBorehole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم حذف الجسة بنجاح";
        state.error = null;
        state.list = state.list.filter(b => b.id !== action.meta.arg);
      })
      .addCase(deleteBorehole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getBoreholeDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.details = null;
      })
      .addCase(getBoreholeDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.details = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getBoreholeDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBoreholesError,
  clearBoreholesSuccess,
  clearBoreholeDetails,
  setBoreholes,
  setBoreholesPagination,
} = boreholesSlice.actions;

export default boreholesSlice.reducer;
